import { IContactCenterPF } from 'src/app/components/pessoa-fisica/interfaces/IContactCenterPF';
import { SdkAcdAgent } from '../sdk-acd/sdk-acd.service';
import { NiceSdkAuthenticationService } from '../sdk-auth/nice-sdk-authentication.service';
import { Subject } from 'rxjs';
import { LoggerService } from 'src/app/core/services/logger.service';
import { CXoneVoiceContact } from '@nice-devone/acd-sdk';
import { Injectable } from '@angular/core';
import { EventHandlerService } from '../../services/event-handler.service';
import {
  VoiceCallDirection,
  VoiceCallEventDTO,
  VoiceCallEventType,
} from 'src/app/components/pessoa-fisica/domain/ContactCenter/VoiceCallEventDTO';
import {
  CXonePopUrl,
  EndSessionRequest,
  VoiceContactStatus,
} from '@nice-devone/common-sdk';
import { AdminService } from '@nice-devone/core-sdk';
import { ContatoEvento } from 'src/app/shared/models/contato-evento.models';
import { CxOneUtils } from '../util/CxOneUtils';
import { LocalStorageUtils } from '../util/LocalStorageUtils';
import { JsonConverterUtils } from '../util/JsonConverterUtils';
import { AgentStateEventDTO } from 'src/app/components/pessoa-fisica/domain/ContactCenter/AgentStateEventDTO';
import {
  DigitalSessionDirection,
  DigitalSessionEndReason,
  DigitalSessionEventDTO,
  DigitalSessionEventType,
} from 'src/app/components/pessoa-fisica/domain/ContactCenter/DigitalSessionEventDTO';
import { SdkDigitalService } from '../sdk-digital/sdk-digital.service';
import {
  CXoneDigitalClient,
  CXoneDigitalContact,
} from '@nice-devone/digital-sdk';
import {
  FocusEventDTO,
  FocusEventSource,
} from 'src/app/components/pessoa-fisica/domain/ContactCenter/FocusEventDTO';
import { quickWebLogger } from '@quickweb/logger';
import { getUserIfoInLocalStorage } from 'src/app/utils/get-user-info-In-localstorage';
import {
  extractPopUrlMediaType,
  getMediaType,
} from 'src/app/utils/cxone-utils';

@Injectable({
  providedIn: 'root',
})
export class ContactCenterPFService implements IContactCenterPF {
  public onVoiceCallEvent$ = new Subject<VoiceCallEventDTO>();
  public onAgentStatusEvent$ = new Subject<AgentStateEventDTO>();
  public onDigitalSessionEvent$ = new Subject<DigitalSessionEventDTO>();
  public onFocusEvent$ = new Subject<FocusEventDTO>();
  private readonly localStorageUtils = new LocalStorageUtils();
  private readonly jsonConverterUtils = new JsonConverterUtils();

  constructor(
    private niceSdkAuthService: NiceSdkAuthenticationService,
    private sdkAcdAgent: SdkAcdAgent,
    private logger: LoggerService,
    private eventHandler: EventHandlerService,
    private sdkDigital: SdkDigitalService,
  ) {}

  public async initializer() {
    await this.niceSdkAuthService.launchAgent();

    await this.sdkAcdAgent.initialize();
    this.sdkAcdAgent.agentState$.subscribe((agentState) => {
      this.AgentStateUpdateHandler(agentState);
    });

    try {
      this.sdkAcdAgent.voiceContactUpdate$.subscribe(
        async (voiceContact: CXoneVoiceContact) => {
          this.VoiceContactUpdateHandler(voiceContact);
        },
      );
    } catch (error) {
      this.logger.warn('Falha inicialização de voz:', `${error}`);
    }

    try {
      await this.sdkDigital.initializeEvents();
      CXoneDigitalClient.instance.digitalContactManager.onDigitalContactEvent.subscribe(
        async (digitalContact: CXoneDigitalContact) => {
          this.DigitalContactHandler(digitalContact);
        },
      );
      this.sdkAcdAgent.instance.screenPop.screenPopSubscribe(
        true,
        true,
        true,
        true,
        true,
        true,
      );
      this.sdkAcdAgent.instance.screenPop.popUrlSubject.subscribe(
        (popUrlData: CXonePopUrl) => {
          this.PopUrlHandler(popUrlData);
        },
      );
    } catch (error) {
      this.logger.warn('Falha inicialização de digital:', `${error}`);
    }
  }

  public async LogoutUser() {
    let endRequest: EndSessionRequest = {
      forceLogoff: true,
      endContacts: false,
      ignorePersonalQueue: true,
    };

    this.sdkAcdAgent.cxoneAcd.session.endSession(endRequest);
  }

  public getAgentName(): string | null {
    try {
      const rawUserInfo = this.localStorageUtils.safeGetItem('user_info') || '';
      const userInfo = this.jsonConverterUtils.JsonParsed(rawUserInfo) as {
        firstName?: string;
      };

      return typeof userInfo?.firstName === 'string'
        ? userInfo.firstName
        : null;
    } catch {
      return null;
    }
  }

  public async AddCrmUserIdOnAttachData(
    interacaoId: string,
    crmUserId: string,
  ): Promise<void> {
    this.logger.info('Enviando usuário encontrado para o script:', crmUserId);

    try {
      await AdminService.instance.signalScript(interacaoId, {
        p1: 'DadosFront',
        p2: 'AddCRMUserId:' + crmUserId,
      });
      this.logger.info(
        'UserId adicionado com sucesso ao attachData no script.',
      );
    } catch (error) {
      this.logger.error(
        'Erro ao enviar adicionar attachData para o script:',
        error,
      );
    }

    return Promise.resolve();
  }

  private VoiceContactUpdateHandler(voiceContact: CXoneVoiceContact) {
    const voiceContactObject = {
      ani: voiceContact.ani,
      dnis: voiceContact.dnis,
      callType: voiceContact.callType,
      agentMuted: voiceContact.agentMuted,
      interactionId: voiceContact.interactionId,
      contactID: voiceContact.contactID,
      masterID: voiceContact.masterID,
      isInbound: voiceContact.isInbound,
      screenPopUrl: voiceContact.screenPopUrl,
      startTime: voiceContact.startTime,
      status: voiceContact.status, // required (*)
      skill: voiceContact.skill,
    };

    let voiceCallEventType = this.GetVoiceCallEventType(voiceContact);

    if (voiceCallEventType === null) {
      this.logger.info(
        `CxOne VoiceContactUpdate descartado: ContactId ${voiceContact.contactID}.`,
      );
      return;
    }

    this.logger.info(
      'Informações do contato de voz recebido: ',
      JSON.stringify(voiceContactObject),
    );

    this.updateEventHandler(voiceContact, voiceCallEventType);
    let VoiceCallEventDTO: VoiceCallEventDTO = {
      Id: voiceContact.contactID || '',
      Ani: voiceContact.ani || '',
      AttachData: CxOneUtils.CreateDictionaryFromPopUrl(
        voiceContact.screenPopUrl,
      ),
      CallDirection: voiceContact.isInbound
        ? VoiceCallDirection.Inbound
        : VoiceCallDirection.Outbound,
      CallStatus: voiceCallEventType,
      DisconectCode: voiceContact.disconnectCode,
    };

    this.onVoiceCallEvent$.next(VoiceCallEventDTO);
  }

  private GetVoiceCallEventType(
    voiceContact: CXoneVoiceContact,
  ): VoiceCallEventType | null {
    const status = voiceContact.status || '';

    this.logger.info(
      `Status do contato: ${voiceContact.status}. ContactId: ${voiceContact.contactID}.`,
    );
    if (
      status.toLowerCase() === VoiceContactStatus.ACTIVE &&
      !this.eventHandler.isProcessed({
        id: voiceContact.contactID,
        status: voiceContact.status,
      }) &&
      voiceContact.screenPopUrl !== ''
    ) {
      return VoiceCallEventType.Answered;
    }

    if (status.toLowerCase() === VoiceContactStatus.DISCONNECTED) {
      this.logger.info('Evento de release.');
      return VoiceCallEventType.Released;
    }

    return null;
  }

  private updateEventHandler(
    voiceContact: CXoneVoiceContact,
    voiceCallEventType: VoiceCallEventType,
  ) {
    let contatoEvento = {
      id: voiceContact.contactID,
      status: voiceContact.status,
    } as ContatoEvento;

    switch (voiceCallEventType) {
      case VoiceCallEventType.Answered: {
        this.eventHandler.add(contatoEvento);
        break;
      }
      case VoiceCallEventType.Released: {
        this.eventHandler.remove(contatoEvento);
        break;
      }
      default: {
        this.logger.warn('Tipo de evento de voz desconhecido.');
        break;
      }
    }
  }

  private AgentStateUpdateHandler(agentState: any) {
    this.logger.info('PF (ACD) - Agent State', `${JSON.stringify(agentState)}`);

    let agentStateDTO: AgentStateEventDTO = {
      agentNextState: agentState.nextState.reason,
      agentCurrentState: agentState.CurrentState,
    };
    this.onAgentStatusEvent$.next(agentStateDTO);
  }

  private async DigitalContactHandler(digitalContact: CXoneDigitalContact) {
    const contactObject = {
      case: digitalContact.case,
      channelType: digitalContact.channelType,
      customerName: digitalContact.customerName,
      interactionId: digitalContact.interactionId,
      isAssignedToAgentInbox: digitalContact.isAssignedToAgentInbox,
      isCaseAssigned: digitalContact.isCaseAssigned,
      startTime: digitalContact.startTime,
      status: digitalContact.status,
      type: digitalContact.type,
    };

    this.logger.info(
      'Digital contact recebido: ',
      JSON.stringify(contactObject),
    );

    if (digitalContact.eventDetails.eventType === 'CaseInboxAssigned') {
      this.DigitalContactCaseAssignedHandler(digitalContact);
    }

    if (digitalContact.eventDetails.eventType === 'CaseInboxUnassigned') {
      this.DigitalContactCaseUnassignedHandler(digitalContact);
    }
  }

  private async DigitalContactCaseAssignedHandler(
    digitalContact: CXoneDigitalContact,
  ) {
    const user = getUserIfoInLocalStorage();

    quickWebLogger.logger.info(
      `Evento de atribuição (Assigned): ${digitalContact.caseId}`,
      {
        details: {
          user,
          masterId: digitalContact.caseId,
        },
      },
    );

    this.logger.info('Evento de atribuição (Assigned)', digitalContact.caseId);
  }

  private DigitalContactCaseUnassignedHandler(
    digitalContact: CXoneDigitalContact,
  ) {
    let digitalSession: DigitalSessionEventDTO = {
      Id: digitalContact.caseId,
      SessionDirection: DigitalSessionDirection.Inbound,
      SessionEventType: DigitalSessionEventType.Unassigned,
      SessionEndReason: DigitalSessionEndReason.Closed,
      contactId: digitalContact.contactID,

      mediaType: getMediaType(digitalContact),
    };
    this.onDigitalSessionEvent$.next(digitalSession);
    return;

    //Transferencia
  }

  private async DigitalContactCaseTransferredHandler(popUrl: CXonePopUrl) {
    const user = getUserIfoInLocalStorage();
    quickWebLogger.logger.info(
      `Evento de atribuição (screenPopUrl): ${popUrl.contactId} `,
      {
        details: {
          user,
          masterId: popUrl.contactId,
          attachData: popUrl,
        },
      },
    );

    this.logger.info(
      `Evento de atribuição (screenPopUrl): ${popUrl.contactId} `,
      {
        details: {
          user,
          contactCenterInteractionId: popUrl.contactId,
          attachData: popUrl,
        },
      },
    );

    const attachData = CxOneUtils.CreateDictionaryFromPopUrl(popUrl.url) ?? {};

    quickWebLogger.logger.info(
      `Evento de atribuição (screenPopUrl) com dados anexados: ${popUrl.contactId} `,
      {
        details: {
          user,
          masterId: popUrl.contactId,
          attachData: attachData,
        },
      },
    );

    this.logger.info(
      `Evento de atribuição (screenPopUrl) com dados anexados: ${popUrl.contactId} `,
      {
        details: {
          user,
          contactCenterInteractionId: popUrl.contactId,
          attachData: attachData,
        },
      },
    );

    const digitalSession: DigitalSessionEventDTO = {
      Id: popUrl.contactId,
      AttachData: attachData,
      SessionDirection: DigitalSessionDirection.Inbound,
      SessionEventType: DigitalSessionEventType.Assigned,
      contactId: popUrl.contactId,
      mediaType: extractPopUrlMediaType(popUrl),
    };

    this.onDigitalSessionEvent$.next(digitalSession);
  }

  private async PopUrlHandler(popUrl: CXonePopUrl) {
    this.logger.info('PopUrl recebido:', JSON.stringify(popUrl));

    if (popUrl.tabTitle?.toUpperCase() === 'ATTACHESDYNAMICS') {
      this.DigitalContactCaseTransferredHandler(popUrl);
    }

    if (popUrl.tabTitle?.toUpperCase() === 'FOCUS') {
      let focusEvent: FocusEventDTO = {
        Id: popUrl.contactId,
        Source: FocusEventSource.ContactCenter,
      };
      this.onFocusEvent$.next(focusEvent);
    }
  }
}
